import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components/macro';
import { NavigationLink } from '../navigationLink/NavigationLink';
import { Button } from '../../button/Button';
import { device } from '../../../Theme';
import { motion, AnimatePresence } from 'framer-motion';
import SimpleBarReact from 'simplebar-react';
import { rgba } from 'polished';
import { genreExists, isGenreMatch } from '../../../../helpers';

import { connect } from 'react-redux';
import { channelsFetchData } from '../../../../actions';
import {
  BOXSET_ID,
  CYW_ID,
  STWNSH_ID,
  HANSH_ID,
  LEARNERS_ID,
  WALTER_ID,
  HERE_ID,
} from '../../../../constants';

import 'simplebar/src/simplebar.css';

export const SideNavigation = forwardRef(
  (
    {
      auth,
      open,
      close,
      strings,
      channels,
      channelsHasErrored,
      categories,
      genres,
      changeLang,
      currentPath,
      handleClick,
      width,
      isMobile,
      ...props
    },
    ref
  ) => {
    const availability = categories.availability;
    const accessibility = categories.accessibility;
    const NavigationLinkContainer = ({ condition, wrapper, children }) =>
      condition ? wrapper(children) : children;
    const Navigation = (variant) => {
      return (
        <>
          <UL1>
            <LI1>
              <NavigationLink
                active={currentPath === '/'}
                icon
                label={strings ? strings.home : 'Home'}
                link="/"
                handleClick={handleClick}
                size="x-large"
              />
            </LI1>
            {auth && (
              <LI1>
                <NavigationLink
                  active={currentPath === '/MyS4C/Watching'}
                  icon
                  label={
                    strings ? strings.watchingContinue : 'Continue watching'
                  }
                  materialLabel="replay"
                  link="/MyS4C/Watching"
                  handleClick={handleClick}
                />
              </LI1>
            )}
            {auth && (
              <LI1>
                <NavigationLink
                  active={currentPath === '/MyS4C/MyList'}
                  icon
                  label={strings ? strings.myList : 'My list'}
                  materialLabel="bookmark"
                  link="/MyS4C/MyList"
                  handleClick={handleClick}
                />
              </LI1>
            )}
            <LI1>
              <NavigationLink
                active={currentPath === '/Live'}
                icon
                label={strings ? strings.watchLive : 'Watch Live'}
                materialLabel="tv"
                link="/Live"
                handleClick={handleClick}
              />
            </LI1>
            <LI1>
              <NavigationLink
                active={
                  currentPath === '/TVSchedule' || currentPath === '/CatchUp'
                }
                icon
                label={strings ? strings.tvSchedule : 'TV Schedule'}
                materialLabel="event"
                link="/TVSchedule"
                handleClick={handleClick}
              />
            </LI1>
            <LI1>
              <NavigationLink
                active={currentPath === '/BoxSets'}
                icon
                label={strings ? strings.boxSets : 'Box sets'}
                materialLabel="layers"
                link="/BoxSets"
                handleClick={handleClick}
              />
            </LI1>
          </UL1>
          <H2>{strings ? strings.explore : 'Explore'}</H2>
          {channels && !channelsHasErrored && Object.keys(channels).length > 0 && (
            <NavigationLinkContainer
              condition={isMobile}
              wrapper={(children) => (
                <SimpleBarReact style={{ width: '100%' }}>
                  {children}
                </SimpleBarReact>
              )}
            >
              <UL2>
                {Object.keys(channels).map((key) => (
                  <LI2 key={key}>
                    {genres &&
                      genreExists(genres, channels[key].categoryId) && (
                        <NavigationLink
                          active={
                            currentPath ===
                            `/Categories/${channels[key].categoryId}`
                          }
                          icon
                          iconType="s4c"
                          s4cImageUrl={channels[key].iconImage.url}
                          label={channels[key].title}
                          link={`/Categories/${channels[key].categoryId}`}
                          handleClick={handleClick}
                          alt={channels[key].title}
                        />
                      )}
                  </LI2>
                ))}
              </UL2>
            </NavigationLinkContainer>
          )}
          <H2>{strings ? strings.categories : 'Categories'}</H2>
          {genres && Object.keys(genres).length > 0 && (
            <NavigationLinkContainer
              condition={isMobile}
              wrapper={(children) => (
                <SimpleBarReact style={{ width: '100%' }}>
                  {children}
                </SimpleBarReact>
              )}
            >
              <UL3>
                {Object.keys(genres).map(
                  (key) =>
                    !isGenreMatch(genres[key].id, BOXSET_ID) &&
                    !isGenreMatch(genres[key].id, CYW_ID) &&
                    !isGenreMatch(genres[key].id, STWNSH_ID) &&
                    !isGenreMatch(genres[key].id, HANSH_ID) &&
                    !isGenreMatch(genres[key].id, LEARNERS_ID) &&
                    !isGenreMatch(genres[key].id, WALTER_ID) &&
                    !isGenreMatch(genres[key].id, HERE_ID) && (
                      <LI2 key={key}>
                        <NavigationLink
                          active={
                            currentPath === `/Categories/${genres[key].id}`
                          }
                          label={genres[key].genre}
                          link={`/Categories/${genres[key].id}`}
                          handleClick={handleClick}
                        />
                      </LI2>
                    )
                )}
                {availability &&
                  Object.keys(availability).length > 0 &&
                  Object.keys(availability).map((key) => (
                    <LI2 key={key}>
                      <NavigationLink
                        active={
                          currentPath === `/Categories/${availability[key].id}`
                        }
                        label={availability[key].genre}
                        link={`/Categories/${availability[key].id}`}
                        handleClick={handleClick}
                      />
                    </LI2>
                  ))}
                {accessibility &&
                  Object.keys(accessibility).length > 0 &&
                  Object.keys(accessibility).map((key) => (
                    <LI2 key={key}>
                      <NavigationLink
                        active={
                          currentPath === `/Categories/${accessibility[key].id}`
                        }
                        label={accessibility[key].genre}
                        link={`/Categories/${accessibility[key].id}`}
                        handleClick={handleClick}
                      />
                    </LI2>
                  ))}
              </UL3>
            </NavigationLinkContainer>
          )}
          <ButtonContainer>
            <Button
              label={strings ? strings.langTextSwitch : 'Cymraeg'}
              type="secondary"
              onClick={() => changeLang()}
              handleClick={handleClick}
            />
          </ButtonContainer>
        </>
      );
    };

    return isMobile ? (
      <AnimatePresence>
        {open && (
          <ClicNavigation
            ref={ref}
            open={open}
            initial={{ height: 0 }}
            animate={{ height: 'calc(100vh - 69px)' }}
            exit={{ height: 0 }}
            transition={{ type: 'tween' }}
          >
            <Navigation />
          </ClicNavigation>
        )}
      </AnimatePresence>
    ) : (
      <ClicNavigation
        ref={ref}
        open={open}
        animate={open ? { width: 292 } : { width: 92 }}
        transition={{ duration: '0' }}
      >
        <SimpleBarReact style={{ height: 'calc(100vh - 105px)' }}>
          <Navigation />
        </SimpleBarReact>
      </ClicNavigation>
    );
  }
);

const mapStateToProps = (state) => {
  return {
    channels: state.channels.success,
    channelsIsLoading: state.channels.loading,
    channelsHasErrored: state.channels.error,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    fetchChannels: () => dispatch(channelsFetchData()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(SideNavigation);

const UL1 = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`;

const UL2 = styled(UL1)``;

const UL3 = styled(UL1)``;

const LI1 = styled.li`
  + li {
    margin-top: 0.6em;
  }
`;

const LI2 = styled(LI1)`
  background-color: ${(props) =>
    props.theme.name === 'light'
      ? props.theme.color.black5
      : props.theme.color.white5};
  padding: 2px;
  border-radius: 4px;
  @media ${device.tablet} {
    background-color: initial;
  }
  &:hover {
    background-color: ${(props) =>
      props.theme.name === 'light'
        ? props.theme.color.black10
        : props.theme.color.white10};
    @media ${device.tablet} {
      background-color: initial;
    }
  }
`;

const H2 = styled.h2`
  font-family: 'Gotham Narrow SSm 4r', 'Gotham Narrow SSm A',
    'Gotham Narrow SSm B', 'HelveticaNeue-Regular', 'Helvetica Neue Regular',
    'Helvetica Neue', Helvetica, Verdana, sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 17px;
  color: ${(props) =>
    props.theme.name === 'light'
      ? props.theme.color.black75
      : props.theme.color.white75};
  margin-top: 12px;
  padding: 0 20px;
  @media ${device.tablet} {
    padding: 0 30px;
  }
`;

const ClicNavigation = styled(motion.nav)`
  overflow: hidden;
  overflow-y: auto;

  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: calc(100vh - 105px);
  padding: 0.5em 0;
  z-index: 1;

  &:before {
    position: absolute;
    top: 0;
    height: 100%;
    z-index: -1;
    content: '';
    @media ${device.tablet} {
      width: 96px;
    }
  }
  ${UL2}, ${UL3} {
    display: flex;
    margin: 0 20px 12px;
    ${LI1} {
      flex: 0 0 auto;
    }
    ${LI1} + ${LI1} {
      margin-top: 0;
      margin-left: 4px;
      @media ${device.tablet} {
        margin-top: 12px;
        margin-left: 0;
      }
    }
  }
  ${UL1} + ${H2} {
    margin-top: 1.5em;
  }
  ${UL2} + ${H2} {
    margin-top: 3.2em;
  }
  ${UL1} {
    padding: 0 2px 0 0;
  }
  ${({ open }) =>
    open &&
    css`
      background: ${(props) =>
        props.theme.name === 'light'
          ? props.theme.color.whiteFull
          : props.theme.color.blackFull};
    `}

  @media ${device.tablet} {
    width: 292px;
    padding-bottom: 1.4em;
    transition: opacity 0.3s ease-out;
    opacity: 1;
    ${({ open }) =>
      open &&
      css`
        &:before {
          content: '';
          display: block;
          top: 0;
          left: 0;
          height: 100%;
          width: 100%;
          background: ${(props) =>
            props.theme.name === 'light'
              ? `linear-gradient(90deg, ${rgba(
                  props.theme.color.whiteFull,
                  0.95
                )} 70%, ${rgba(props.theme.color.whiteFull, 0)} 100%),
            linear-gradient(90deg, ${rgba(
              props.theme.color.whiteFull,
              1
            )} 0%, ${rgba(props.theme.color.whiteFull, 0)} 100%)`
              : `linear-gradient(90deg, ${rgba(
                  props.theme.color.blackFull,
                  0.95
                )} 70%, ${rgba(props.theme.color.blackFull, 0)} 100%),
            linear-gradient(90deg, ${rgba(
              props.theme.color.blackFull,
              1
            )} 0%, ${rgba(props.theme.color.blackFull, 0)} 100%)`};
        }
      `}

    ${UL1}, ${UL2} {
      display: flex;
      flex-direction: column;
      margin: 0;
      padding: 0 2px;
      ${LI1}:first-of-type {
        margin-top: 2px;
      }
    }

    ${UL1}:after,
    ${UL2}:after {
      display: flex;
      margin: 18px 32px 12px;
      border-bottom: 1px solid
        ${(props) =>
          props.theme.name === 'light'
            ? props.theme.color.black90
            : props.theme.color.white90};
      opacity: 0.1;
      content: '';
      ${LI1}:last-of-type {
        margin-bottom: 1.2em;
      }
    }

    ${UL1} {
      + ${H2} {
        margin-top: initial;
        margin-bottom: 1.3em !important;
      }
      ${LI1} + ${LI1} {
        margin-top: 12px;
      }
    }

    ${UL2} {
      + ${H2} {
        margin-top: 0.83em;
        margin-bottom: 0.7em;
      }
      &:after {
        margin-bottom: 0;
      }
    }

    ${UL3} {
      ${LI1} + ${LI1} {2x
        margin-top: 0;
      }
      a {
        height: auto;
      }
    }

    ${UL1},
    ${UL2},
    ${UL3} {
      padding: 2px 2px 0 0;
      overflow: hidden;
    }

    backdrop-filter: unset;

    ${({ open }) =>
      open &&
      css`
        background: ${(props) =>
          props.theme.name === 'light'
            ? `linear-gradient(90deg, ${rgba(
                props.theme.color.whiteFull,
                0.95
              )} 70%, ${rgba(props.theme.color.whiteFull, 0)} 100%),
              linear-gradient(90deg, ${rgba(
                props.theme.color.whiteFull,
                1
              )} 0%, ${rgba(props.theme.color.whiteFull, 0)} 100%)`
            : `linear-gradient(90deg, ${rgba(
                props.theme.color.blackFull,
                0.95
              )} 70%, ${rgba(props.theme.color.blackFull, 0)} 100%),
              linear-gradient(90deg, ${rgba(
                props.theme.color.blackFull,
                1
              )} 0%, ${rgba(props.theme.color.blackFull, 0)} 100%)`};
      `}

    ${({ open }) =>
      !open &&
      css`
        width: 96px;
        background: ${(props) =>
          props.theme.name === 'light'
            ? `linear-gradient(90deg, ${rgba(
                props.theme.color.whiteFull,
                1
              )} 60%, ${rgba(props.theme.color.whiteFull, 0)} 90%)`
            : `linear-gradient(90deg, ${rgba(
                props.theme.color.blackFull,
                1
              )} 60%, ${rgba(props.theme.color.blackFull, 0)} 90%)`};
        opacity: 1;
        ${UL2} {
          margin-top: 2.2em;
          overflow: hidden;
        }
        ${H2}, ${UL3} {
          display: none;
        }
      `}
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 2em 0;
  padding-bottom: 2em;
  @media ${device.tablet} {
    display: none;
  }
`;
